<template>
  <button
    class="souvenir-btn btn"
    :class="{ sent, error: sendState === 'error' }"
    :aria-label="sent ? 'Souvenir Sent' : 'Send Souvenir'"
    @click="onClaim"
  >
    <FontAwesomeIcon v-if="sent" icon="check" size="lg" />
    <FontAwesomeIcon v-else-if="sendState === 'error'" icon="arrow-rotate-right" size="lg" />
    <span v-else v-text="sendText" />
  </button>
  <Teleport to="body">
    <div
      class="modal-overlay"
      :class="{ 'modal-overlay-visible': modalVisible }"
      @click="dismissModal"
    />
    <div
      ref="cardBodyRef"
      class="modal-card flex-center text-center"
      :class="{ 'modal-card-visible': modalVisible }"
    >
      <div class="data-form flex-column">
        <div
          data-test="email-field"
          data-sentry-mask
          class="field"
          @click="focusOn(emailRef)"
        >
          <label v-text="t('enterEmail')" />
          <input
            ref="emailRef"
            v-model="email"
            aria-label="Email"
            type="email"
          >
        </div>
        <div
          data-test="languages-field"
          class="field checkbox-group-field"
        >
          <label v-text="t('selectLanguage')" />
          <div
            v-for="language in souvenirLanguages"
            :key="language.value"
            class="checkbox"
          >
            <input
              v-model="languageSelections[language.value]"
              type="checkbox"
              :disabled="language.disabled"
            >
            <label @click="toggleLanguage(language)" v-text="language.text" />
          </div>
        </div>
        <button
          data-test="continue-btn"
          class="btn"
          :disabled="!formValid"
          @click="onContinue"
          v-text="t('saveAndContinue')"
        />
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { DeepReadonly, PropType, computed, defineComponent, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useDataStore } from '@/stores/data'
import { useLocalStorageStore } from '@/stores/local-storage'
import { useMqttStore } from '@/stores/mqtt'

import { Exhibit, LanguageSouvenirs } from 'types/data'
import { EmailMessagePayload } from 'types/mqtt-messages'

export default defineComponent({
  props: {
    exhibit: { type: Object as PropType<DeepReadonly<Exhibit>>, required: true },
  },

  setup (props) {
    const { t } = useI18n()
    const mqttStore = useMqttStore()
    const localStorageStore = useLocalStorageStore()

    const dataStore = useDataStore()
    const souvenirId = computed(() => props.exhibit.souvenirId as number)
    const souvenirLanguages = computed(() => {
      const selectedLanguage = localStorageStore.getUserData()?.language
      const languageTranslations = dataStore.languages.find((l) => l.symbol === selectedLanguage)?.languages ?? dataStore.languages
      return dataStore.languageSouvenirs
        .reduce<DeepReadonly<LanguageSouvenirs>[]>((acc, souvenirLanguage) => {
          if (souvenirLanguage.souvenirs?.find(e => e.souvenirId === souvenirId.value)) {
            acc.push(souvenirLanguage)
          }
          return acc
        }, [])
        .map((lang) => {
          const l = languageTranslations.find((l) => l.symbol === lang.symbol)!
          return {
            text: l.symbol === selectedLanguage ? l.display! : `${l.display} (${l.native})`,
            value: l.symbol ?? '',
            disabled: historyItem.value.languages?.includes(l.symbol!) ?? false,
          }
        })
    })
    const languageSelections: Record<string, boolean> = reactive({})
    if (localStorageStore.getUserData()?.language) {
      languageSelections[localStorageStore.getUserData()!.language] = true
    }
    function toggleLanguage (language: { text: string, value: string, disabled: boolean }): void {
      if (language.disabled) return
      languageSelections[language.value] = !languageSelections[language.value]
    }

    const historyItem = computed(() => localStorageStore.souvenirHistory.getItem(souvenirId.value))

    const email = ref(localStorageStore.getUserData()?.email ?? '')

    const modalVisible = ref(false)
    function dismissModal (): void {
      modalVisible.value = !modalVisible.value
    }
    const emailRef = ref<HTMLInputElement | null>(null)
    function focusOn (el: HTMLSelectElement | HTMLInputElement | null): void {
      if (!el) return
      el.focus()
    }

    const formValid = computed(() => {
      if (!email.value) return false
      if (!Object.values(languageSelections).some(v => v)) return false
      return true
    })

    const sent = computed(() => {
      if (!historyItem.value.sent) return false
      // this could be undefined as this key was added later
      const languages = historyItem.value.languages ?? []
      return souvenirLanguages.value.some((lang) => languages.includes(lang.value))
    })
    const sendState = ref<'idle' | 'sending' | 'error'>('idle')
    const sendText = computed(() => {
      if (sent.value) return ''
      if (sendState.value === 'sending') return '. . .'
      return t('claimYourSouvenir')
    })
    async function sendEmail (): Promise<void> {
      const userData = localStorageStore.getUserData()
      if (!userData) return
      sendState.value = 'sending'
      const promises = souvenirLanguages.value
        .reduce<Promise<{ err: unknown, languageCode: string }>[]>((acc, lang) => {
          if (!lang.disabled && languageSelections[lang.value]) {
            acc.push(new Promise((resolve) => {
              mqttStore.publish<EmailMessagePayload>(
                'email',
                {
                  email: userData.email,
                  languageCode: lang.value,
                  souvenirId: souvenirId.value,
                },
                { qos: 2 },
                (err) => {
                  resolve({
                    err,
                    languageCode: lang.value,
                  })
                },
              )
            }))
          }
          return acc
        }, [])

      const results = await Promise.all(promises)
      sendState.value = 'idle'
      const successLanguages = historyItem.value.languages ?? []
      for (const result of results) {
        if (result.err) {
          console.error(result.err)
          sendState.value = 'error'
          return
        }
        successLanguages.push(result.languageCode)
      }

      localStorageStore.souvenirHistory.setItem(
        souvenirId.value,
        {
          sent: !!successLanguages.length,
          languages: Object.entries(languageSelections)
            .reduce<string[]>((acc, [k, v]) => {
              if (v) acc.push(k)
              return acc
            }, []),
        },
      )
    }
    const cardBodyRef = ref<HTMLElement | null>(null)
    async function onClaim (): Promise<void> {
      modalVisible.value = true
      cardBodyRef.value?.scrollTo(0, 0)
    }
    async function onContinue (): Promise<void> {
      if (!formValid.value) return
      dismissModal()
      await sendEmail()
    }

    return {
      t,
      email,
      souvenirLanguages,
      languageSelections,
      toggleLanguage,
      modalVisible,
      dismissModal,
      emailRef,
      focusOn,
      formValid,
      sent,
      sendState,
      sendText,
      cardBodyRef,
      onClaim,
      onContinue,
    }
  },
})
</script>

<style lang="scss" scoped>
.modal-card {
  padding: 32px 36px 40px;
}
.souvenir-btn {
  margin: 24px auto;
  width: 300px;
  &.sent {
    background-color: var(--success);
  }
  &.error {
    background-color: var(--error);
  }
}
</style>
